import React from "react";
import {
  Calendar,
  Download,
  Eye,
  Clock,
  CheckCircle,
  XCircle,
  FileText,
  User,
} from "lucide-react";
import { useTranslation } from "react-i18next";

type BatchStatus = "draft" | "processing" | "completed" | "failed";

export type BatchPayment = {
  id: string;
  code: string;
  name: string;
  month: string;
  status: BatchStatus;
  totalPayments: number;
  totalAmount: number;
  createdBy: string;
  createdAt: string;
  completedAt?: string;
  note?: string;
};

interface BatchCardProps {
  batch: BatchPayment;
  onView: (batchId: string) => void;
  onDownload?: (batchId: string) => void;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat("vi-VN").format(amount) + " đ";
};

const BatchCard: React.FC<BatchCardProps> = ({ batch, onView, onDownload }) => {
  const { t } = useTranslation();

  const getStatusBadge = (status: BatchStatus) => {
    const badges = {
      draft: {
        bg: "bg-gray-100 dark:bg-gray-700",
        text: "text-gray-700 dark:text-gray-300",
        icon: FileText,
        label: t("batchHistory.statusDraft"),
      },
      processing: {
        bg: "bg-blue-100 dark:bg-blue-900/30",
        text: "text-blue-700 dark:text-blue-400",
        icon: Clock,
        label: t("batchHistory.statusProcessing"),
      },
      completed: {
        bg: "bg-green-100 dark:bg-green-900/30",
        text: "text-green-700 dark:text-green-400",
        icon: CheckCircle,
        label: t("batchHistory.statusCompleted"),
      },
      failed: {
        bg: "bg-red-100 dark:bg-red-900/30",
        text: "text-red-700 dark:text-red-400",
        icon: XCircle,
        label: t("batchHistory.statusFailed"),
      },
    };
    const badge = badges[status];
    const Icon = badge.icon;
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {badge.label}
      </span>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full">
      {/* Card Content */}
      <div className="p-4 flex-1 flex flex-col">
        {/* Header: Status + Code */}
        <div className="flex items-start justify-between mb-3">
          {getStatusBadge(batch.status)}
          <span className="text-xs text-gray-400 dark:text-gray-500">
            {batch.code}
          </span>
        </div>

        {/* Title */}
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2 min-h-[40px]">
          {batch.name}
        </h3>

        {/* Month */}
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 mb-3">
          <Calendar className="w-4 h-4" />
          <span>Tháng {batch.month}</span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
              {t("batchHistory.numPayments")}
            </p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">{batch.totalPayments}</p>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
              {t("batchHistory.totalAmount")}
            </p>
            <p className="text-xl font-bold text-green-600 dark:text-green-400">
              {formatCurrency(batch.totalAmount)}
            </p>
          </div>
        </div>

        {/* Creator Info */}
        <div className="space-y-1.5 text-sm text-gray-500 dark:text-gray-400 mb-3">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">{batch.createdBy}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>{batch.createdAt}</span>
          </div>
          {batch.completedAt && (
            <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              <span>{batch.completedAt}</span>
            </div>
          )}
        </div>

        {/* Note */}
        <div className="mt-auto">
          {batch.note ? (
            <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-2.5 min-h-[52px]">
              <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">{batch.note}</p>
            </div>
          ) : (
            <div className="min-h-[52px]" />
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 p-4 pt-0">
        <button
          onClick={() => onView(batch.id)}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-sm font-medium rounded-md transition-colors"
        >
          <Eye className="w-4 h-4" />
          {t("batchHistory.view")}
        </button>
        <button
          onClick={() => onDownload?.(batch.id)}
          className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 text-gray-400 hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default BatchCard;
